import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, ExternalLink, Users } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "Novo", color: "bg-blue-100 text-blue-800" },
  { value: "assigned", label: "Atribuído", color: "bg-amber-100 text-amber-800" },
  { value: "contacted", label: "Contactado", color: "bg-purple-100 text-purple-800" },
  { value: "closed", label: "Fechado", color: "bg-emerald-100 text-emerald-800" }
];

const AdminConsultancyRequests = () => {
  const [referrals, setReferrals] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateData, setUpdateData] = useState({ consultant_id: "none", status: "new", notes_admin: "" });
  const [consultantCityFilter, setConsultantCityFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [referralsRes, consultantsRes] = await Promise.all([
        axios.get(`${API}/referrals?source=client_request`, { withCredentials: true }),
        axios.get(`${API}/consultants?active_only=true`, { withCredentials: true })
      ]);
      setReferrals(referralsRes.data.referrals || []);
      setConsultants(consultantsRes.data.consultants || []);
    } catch (error) {
      console.error("Error fetching consultancy requests:", error);
      toast.error("Erro ao carregar solicitações de consultoria");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status);
    return <Badge className={option?.color || "bg-slate-100"}>{option?.label || status}</Badge>;
  };

  const getConsultantsForAreaAndCity = (area, city = "all") => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const normalizedCity = normalize(city);
    const areaConsultants = consultants.filter((c) =>
      (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
    );
    if (city === "all") return areaConsultants;
    if (city === "online") return areaConsultants.filter((c) => c.online_only);
    return areaConsultants.filter((c) => normalize(c.city) === normalizedCity);
  };

  const getAvailableCities = (area) => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const areaConsultants = consultants.filter((c) =>
      (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
    );
    const citySet = new Set(areaConsultants.map((c) => (c.city || "").trim()).filter(Boolean));
    const cities = Array.from(citySet).sort((a, b) => a.localeCompare(b, "pt-BR"));
    const hasOnlineOnly = areaConsultants.some((c) => c.online_only);
    if (hasOnlineOnly) {
      cities.push("Online");
    }
    return cities;
  };

  const openUpdateDialog = (referral) => {
    setSelectedReferral(referral);
    setUpdateData({
      consultant_id: referral.consultant_id || "none",
      status: referral.status || "new",
      notes_admin: referral.notes_admin || ""
    });
    setConsultantCityFilter("all");
    setShowUpdateDialog(true);
  };

  const handleUpdateReferral = async () => {
    if (!selectedReferral) return;

    try {
      const payload = {
        status: updateData.status,
        notes_admin: updateData.notes_admin
      };

      if (updateData.consultant_id && updateData.consultant_id !== "none") {
        payload.consultant_id = updateData.consultant_id;
      } else {
        payload.consultant_id = "none";
      }

      await axios.put(`${API}/referrals/${selectedReferral.referral_id}`, payload, { withCredentials: true });
      toast.success("Solicitação atualizada com sucesso!");
      setShowUpdateDialog(false);
      setSelectedReferral(null);
      fetchData();
    } catch (error) {
      console.error("Error updating consultancy request:", error);
      toast.error("Erro ao atualizar solicitação");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold text-navy">Empresas que Solicitaram Consultoria</h1>
          <Link to="/admin" className="text-sm text-navy hover:underline">Voltar ao Dashboard</Link>
        </div>

        {referrals.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nenhuma solicitação de cliente encontrada.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {referrals.map((referral) => (
              <Card key={referral.referral_id} className="card-hover">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-navy/10 rounded flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-navy" />
                      </div>
                      <div>
                        <h3 className="font-medium text-navy">{referral.company_name}</h3>
                        <p className="text-sm text-slate-600">
                          Área solicitada: <span className="font-medium">{referral.area}</span>
                        </p>
                        {referral.consultant_name && (
                          <p className="text-sm text-slate-500">Consultor atual: {referral.consultant_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(referral.status)}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openUpdateDialog(referral)}
                        data-testid={`manage-client-request-${referral.referral_id}`}
                      >
                        Direcionar
                      </Button>
                      <Link to={`/admin/results/${referral.diagnostic_id}`}>
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Direcionar Solicitação</DialogTitle>
            </DialogHeader>
            {selectedReferral && (
              <div className="space-y-4 mt-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Empresa</p>
                  <p className="font-medium">{selectedReferral.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Área</p>
                  <p className="font-medium">{selectedReferral.area}</p>
                </div>

                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Select
                    value={consultantCityFilter}
                    onValueChange={(v) => {
                      setConsultantCityFilter(v);
                      setUpdateData({ ...updateData, consultant_id: "none" });
                    }}
                  >
                    <SelectTrigger data-testid="select-client-request-consultant-city">
                      <SelectValue placeholder="Filtrar por cidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as cidades</SelectItem>
                      {getAvailableCities(selectedReferral.area).map((city) => (
                        <SelectItem key={city} value={city === "Online" ? "online" : city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Consultor</Label>
                  <Select value={updateData.consultant_id} onValueChange={(v) => setUpdateData({ ...updateData, consultant_id: v })}>
                    <SelectTrigger data-testid="select-client-request-consultant">
                      <SelectValue placeholder="Selecione um consultor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {getConsultantsForAreaAndCity(selectedReferral.area, consultantCityFilter).map((c) => (
                        <SelectItem key={c.consultant_id} value={c.consultant_id}>{c.user_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                    <SelectTrigger data-testid="select-client-request-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Observações Admin</Label>
                  <Textarea
                    value={updateData.notes_admin}
                    onChange={(e) => setUpdateData({ ...updateData, notes_admin: e.target.value })}
                    placeholder="Orientações internas..."
                    data-testid="client-request-notes-admin"
                  />
                </div>

                <Button className="w-full bg-navy hover:bg-navy/90" onClick={handleUpdateReferral}>
                  Salvar Direcionamento
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminConsultancyRequests;
